import { useState } from "react";

import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import {
    ChartLegend,
    ChartSwatch,
    EmptyState,
    SegmentedControl,
    Svg,
    TrendChart,
    TrendChartBucket,
    TrendChartSeries,
} from "@impero-com/ui";
import chartLineSvg from "@impero-com/ui/img/chart-line.svg";
import { useNavigate } from "react-router-dom";

import { ActivityFrictionCounts, ActivityTrend } from "api/generated/model";

import {
    ACTIVITY_HEALTH_LEVELS,
    ActivityHealthLevelSpec,
} from "./activityHealth";
import { buildBucketLabels, formatBucketTooltip } from "./activityTrendLabels";
import {
    ActivityTrendSeverity,
    activityTrendStorage,
} from "./activityTrendStorage";
import * as theme from "./ControlHealthActivityTrendWidget.css";
import { ControlHealthWidget } from "./ControlHealthWidget";
import {
    ACTIVITY_POINT_PENDO,
    ACTIVITY_TAB_PENDO,
    CONTROL_HEALTH_PENDO,
} from "./pendo";
import { useBuildTrendBucketUrl } from "./useBuildTrendBucketUrl";

interface ControlHealthActivityTrendWidgetProps {
    trend: ActivityTrend;
    rangeLabel: string;
}

export function ControlHealthActivityTrendWidget({
    trend,
    rangeLabel,
}: ControlHealthActivityTrendWidgetProps) {
    const { t } = useTranslation();
    const [severity, setSeverity] = useState(() =>
        activityTrendStorage.getOr("activityTrendSeverity", "all")
    );
    const levels = ACTIVITY_HEALTH_LEVELS.filter(
        ({ key }) => severity === "all" || key === severity
    );
    const total = levels.reduce((sum, { key }) => sum + trend.totals[key], 0);
    const severityOptions: { value: ActivityTrendSeverity; label: string }[] = [
        { value: "all", label: t("Controls_All") },
        ...ACTIVITY_HEALTH_LEVELS.map(({ key, label }) => ({
            value: key,
            label: label(t),
        })),
    ];

    const onChange = (value: ActivityTrendSeverity) => {
        setSeverity(value);
        activityTrendStorage.set("activityTrendSeverity", value);
    };

    return (
        <ControlHealthWidget
            data-pendo={CONTROL_HEALTH_PENDO.activityCard}
            collapsePendoId={CONTROL_HEALTH_PENDO.activityCollapse}
            title={t("ControlHealth_ActivityTrend_Title")}
            info={t("ControlHealth_ActivityTrend_Info")}
            actions={
                <SegmentedControl
                    aria-label={t("ControlHealth_ActivityTrend_Title")}
                    options={severityOptions.map((option) => ({
                        ...option,
                        "data-pendo": ACTIVITY_TAB_PENDO[option.value],
                    }))}
                    value={severity}
                    onChange={onChange}
                />
            }
            headline={
                trend.buckets.length > 0 && (
                    <ActivityTrendHeadline
                        trend={trend}
                        total={total}
                        rangeLabel={rangeLabel}
                    />
                )
            }
            collapsible
            className={theme.card}
        >
            <ActivityTrendBody
                trend={trend}
                levels={levels}
                total={total}
                severity={severity}
            />
        </ControlHealthWidget>
    );
}

interface ActivityTrendBodyProps {
    trend: ActivityTrend;
    severity: ActivityTrendSeverity;
    levels: ActivityHealthLevelSpec[];
    total: number;
}

function ActivityTrendBody({
    trend,
    levels,
    total,
    severity,
}: ActivityTrendBodyProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const buildBucketUrl = useBuildTrendBucketUrl(severity);

    if (total === 0) {
        return (
            <ActivityTrendPlaceholder
                title={t("ControlHealth_ActivityTrend_Empty_Title")}
                description={t("ControlHealth_ActivityTrend_Empty_Description")}
            />
        );
    }

    const labels = buildBucketLabels(trend.buckets, trend.granularity);
    const data: TrendChartBucket[] = trend.buckets.map((bucket, index) => ({
        label: labels[index],
        values: { ...bucket.counts },
    }));
    const spanByLabel = new Map(
        trend.buckets.map((bucket, index) => [
            labels[index],
            formatBucketTooltip(bucket, trend.granularity, t),
        ])
    );

    const openBucket = (index: number) => {
        const bucket = trend.buckets[index];
        if (bucket !== undefined) {
            navigate(buildBucketUrl(bucket));
        }
    };

    return (
        <div className={theme.body}>
            <div>
                <TrendChart
                    series={buildChartSeries(t, levels, severity)}
                    data={data}
                    height={theme.PLOT_HEIGHT}
                    onBucketClick={openBucket}
                    renderTooltip={(bucket, series) => (
                        <ActivityTrendTooltip
                            bucket={bucket}
                            series={series}
                            span={spanByLabel.get(bucket.label) ?? bucket.label}
                        />
                    )}
                />
            </div>
            <div className={theme.xAxisTitle}>
                {t("ControlHealth_FrictionBreakdown_XAxisTitle")}
            </div>
            <ActivityTrendHealthMarks totals={trend.totals} levels={levels} />
        </div>
    );
}

interface ActivityTrendHeadlineProps {
    trend: ActivityTrend;
    total: number;
    rangeLabel: string;
}

function ActivityTrendHeadline({
    trend,
    total,
    rangeLabel,
}: ActivityTrendHeadlineProps) {
    const { t } = useTranslation();

    const { buckets } = trend;
    const caption =
        buckets.length === 1 && buckets[0].start === buckets[0].end
            ? t("ControlHealth_ActivityTrend_HeadlineCaptionDay", {
                  date: rangeLabel,
              })
            : t("ControlHealth_ActivityTrend_HeadlineCaption", {
                  range: rangeLabel,
              });

    return (
        <p className={theme.headline}>
            <span className={theme.headlineValue}>
                {total.toLocaleString()}
            </span>
            <span className={theme.headlineCaption}>{caption}</span>
        </p>
    );
}

interface ActivityTrendHealthMarksProps {
    totals: ActivityFrictionCounts;
    levels: ActivityHealthLevelSpec[];
}

function ActivityTrendHealthMarks({
    totals,
    levels,
}: ActivityTrendHealthMarksProps) {
    const { t } = useTranslation();

    return (
        <ChartLegend
            layout="inline"
            heading={t("ControlHealth_ActivityTrend_HealthMarks")}
            items={levels.map(({ key, label, color }) => ({
                key,
                label: label(t),
                value: totals[key],
                color,
            }))}
        />
    );
}

interface ActivityTrendTooltipProps {
    bucket: TrendChartBucket;
    series: TrendChartSeries[];
    span: string;
}

function ActivityTrendTooltip({
    bucket,
    series,
    span,
}: ActivityTrendTooltipProps) {
    const { t } = useTranslation();
    const rows = ACTIVITY_HEALTH_LEVELS.flatMap(({ key }) => {
        const entry = series.find((candidate) => candidate.key === key);
        return entry === undefined ? [] : [entry];
    });

    return (
        <div className={theme.tooltip}>
            <span className={theme.tooltipHeading}>{span}</span>
            {rows.map(({ key, label, color }) => (
                <div key={key} className={theme.tooltipRow}>
                    <ChartSwatch color={color} ringed />
                    <span>{label}</span>
                    <span className={theme.tooltipValue}>
                        {(bucket.values[key] ?? 0).toLocaleString()}
                    </span>
                </div>
            ))}
            <span>{t("ControlHealth_ActivityTrend_ClickToView")}</span>
        </div>
    );
}

interface ActivityTrendPlaceholderProps {
    title: string;
    description: string;
}

// The plot's height is held so the card does not jump as the range changes.
function ActivityTrendPlaceholder({
    title,
    description,
}: ActivityTrendPlaceholderProps) {
    return (
        <div className={theme.placeholder}>
            <EmptyState
                title={title}
                description={description}
                icon={<Svg src={chartLineSvg} />}
            />
        </div>
    );
}

function buildChartSeries(
    t: TFunction,
    levels: ActivityHealthLevelSpec[],
    severity: ActivityTrendSeverity
): TrendChartSeries[] {
    return [...levels].reverse().map(({ key, label, color }) => ({
        key,
        label: label(t),
        color,
        "data-pendo":
            ACTIVITY_POINT_PENDO[severity === "all" ? "all" : "severity"][key],
    }));
}
